import { FC, useEffect, useState } from "react";

import { ErrorModal } from "./ErrorModal";
import { onErrorDialog } from "../../../utility/errorDialogBus";
import type { ReportedError } from "../../../utility/reportError";

/**
 * THE ONE ERROR DIALOG, MOUNTED ONCE, FOR EVERY FAILURE IN THE APP.
 *
 * `ErrorModal` used to be state owned by `JobAction`, so only the nine failures that component
 * runs could ever be shown in it; the other ~73 `reportError` call sites had a toast and nothing
 * else. A toast is a bad surface for a failure: it disappears on a timer, it cannot be re-read,
 * and an operator who looked away has lost the code support will ask them for. So every failure
 * comes here now, and the toast survives only as the fallback for a tree with no host.
 *
 * ⚠️ MOUNT IT AS A SIBLING OF `<Suspense>` IN `App.tsx`, exactly like `SupportAssistantHost` and
 * for the same reason: every page is `React.lazy` behind one boundary, so a first navigation
 * suspends and replaces that whole subtree — a dialog mounted inside it would vanish mid-read.
 *
 * ⚠️ AND MOUNT IT SEPARATELY FROM `SupportAssistantHost`, NOT INSIDE IT. That host deliberately
 * renders nothing on `/login`, `/logout`, `/diagnostic/*` and before the terms are accepted.
 * Those are screens where help is not offered — they are not screens where failures stop
 * happening, and a failed sign-in is one of the failures a user most needs to be able to read.
 */

/** The dialog currently up: the failure it is about, plus the codes folded into it. */
interface Raised {
  readonly primary: ReportedError;
  /** Distinct codes of OTHER failures that arrived while this dialog was open. */
  readonly also: readonly string[];
}

const ErrorDialogHost: FC = () => {
  const [raised, setRaised] = useState<Raised | null>(null);

  useEffect(
    () =>
      onErrorDialog((reported) =>
        setRaised((open) => {
          // Nothing up: this failure becomes the dialog.
          if (!open) return { primary: reported, also: [] };

          /**
           * ⚠️ A SECOND FAILURE FOLDS INTO THE OPEN DIALOG; IT DOES NOT STACK OR QUEUE ONE.
           *
           * This is what makes "always a dialog" safe. Failures arrive in BURSTS, because
           * screens load several things at once: `SystemPage` has nine independent option
           * loads each with its own catch, so a BFF outage there used to mean nine toasts and
           * would otherwise mean nine dialogs to dismiss — all of them `CSM-SET-002`, all of
           * them the same outage. `CSMWriteBack` fires three different loads on mount.
           *
           * Folding is not lossy guesswork, it is what the situation actually is: the dialog is
           * modal, so the user cannot have DONE anything between the first failure and this
           * one. Anything arriving while it is up is necessarily from the same burst of
           * already-in-flight requests. A distinct code is still named on the dialog, and every
           * one of them is in the log in full regardless.
           */
          if (reported.code === open.primary.code || open.also.includes(reported.code)) {
            return open;
          }

          return { ...open, also: [...open.also, reported.code] };
        })
      ),
    []
  );

  if (!raised) return null;

  return (
    <ErrorModal
      open
      error={raised.primary.message}
      also={raised.also}
      // The dialog decides for itself whether the assistant is reachable; it is handed the
      // question either way. The PRIMARY failure's question, not a merged one — the assistant
      // answers about one error well and about a list of them badly.
      help={{
        question: raised.primary.question,
        diagnostics: raised.primary.diagnostics,
      }}
      onClose={() => setRaised(null)}
    />
  );
};

export default ErrorDialogHost;
